import type { CompanyAddressDTO, OrderAddressDTO, PickupDTO } from 'ecosystem'
import type { RememberedAddressChoice } from '../../../hooks/useRememberedAddress'

/**
 * Map a `CompanyAddressDTO` (under `customer.companyProfile.addresses`) into
 * the `OrderAddressDTO` shape order endpoints expect. The two shapes overlap
 * on most fields; the company address carries `phone` where the order
 * address calls it `mobilePhone`, and a few company-side fields (id, type,
 * label, email, default) are dropped because the order DTO has no home for
 * them.
 */
export const companyAddressToOrderAddress = (a: CompanyAddressDTO): OrderAddressDTO => ({
  address1: a.address1 ?? '',
  address2: a.address2 ?? '',
  attention: a.attention ?? '',
  country: a.country ?? '',
  mobilePhone: a.phone ?? '',
  postalCode: a.postalCode ?? '',
  postalRegion: a.postalRegion ?? '',
  reference: a.reference ?? ''
})

const pickDefaultByType = (
  addresses: CompanyAddressDTO[] | undefined,
  type: 'BILLING' | 'SHIPPING'
): CompanyAddressDTO | undefined => {
  if (!addresses?.length) return undefined
  const filtered = addresses.filter((a) => a.type === type)
  return filtered.find((a) => a.default) ?? filtered[0]
}

/**
 * The company address that *is* this address, if any.
 *
 * One matcher, used both by the picker (to light up the right radio) and by the
 * write-on-provision path (to record a pick as an id rather than a copy). They disagreed
 * once — provision compared four fields where the picker compared five — and the looser
 * side quietly reclassified a shopper's custom address as a company one, dropping
 * whatever made it different. Every field a shopper can vary is compared, normalised for
 * the `'' | undefined` the two DTOs disagree about.
 */
export const findMatchingCompanyAddress = (
  address: OrderAddressDTO | undefined,
  companyAddresses: CompanyAddressDTO[] | undefined
): CompanyAddressDTO | undefined => {
  if (!address) return undefined

  return companyAddresses?.find((candidate) => {
    const mapped = companyAddressToOrderAddress(candidate)
    return (
      mapped.address1 === (address.address1 ?? '') &&
      mapped.address2 === (address.address2 ?? '') &&
      mapped.attention === (address.attention ?? '') &&
      mapped.postalCode === (address.postalCode ?? '') &&
      mapped.postalRegion === (address.postalRegion ?? '') &&
      mapped.country === (address.country ?? '')
    )
  })
}

/**
 * Resolve a remembered choice into an address, or `undefined` when it no longer holds.
 *
 * A company pick is looked up in the *current* address list, so an address edited in the
 * account is followed and one deleted from it simply falls through to the default — the
 * account stays the source of truth for its own data, and we only remember the pick.
 */
export const resolveRememberedChoice = <T extends OrderAddressDTO>(
  choice: RememberedAddressChoice<T> | undefined,
  companyAddresses: CompanyAddressDTO[] | undefined
): T | OrderAddressDTO | undefined => {
  if (!choice) return undefined

  if (choice.source === 'company') {
    const match = companyAddresses?.find((a) => a.id === choice.companyAddressId)
    return match ? companyAddressToOrderAddress(match) : undefined
  }

  return isSelectableAddress(choice.address) ? choice.address : undefined
}

/**
 * Whether an address is complete enough to select on the shopper's behalf and render in
 * the folded card.
 *
 * Guards the device-remembered path. Entries written today always come from a validated
 * form submit or a provisioned order, so they pass; the check exists so a partial entry
 * left by an older build can never present itself as a chosen delivery address.
 */
export const isSelectableAddress = (a?: OrderAddressDTO): boolean =>
  Boolean(a?.address1 && a.postalCode && a.postalRegion && a.country)

/** Pick the default BILLING address from a `companyProfile.addresses` list. */
export const pickDefaultBillingAddress = (addresses: CompanyAddressDTO[] | undefined) =>
  pickDefaultByType(addresses, 'BILLING')

/** Pick the default SHIPPING address from a `companyProfile.addresses` list. */
export const pickDefaultShippingAddress = (addresses: CompanyAddressDTO[] | undefined) =>
  pickDefaultByType(addresses, 'SHIPPING')

/**
 * Flatten a pickup's address into the `OrderAddressDTO` shape so the FE
 * can treat a pickup choice the same as a delivery address downstream
 * (e.g. as the canonical `shippingAddress` when PICKUP is selected).
 */
export const pickupToOrderAddress = (p: PickupDTO): OrderAddressDTO => ({
  address1: p.address.address1 ?? '',
  address2: p.address.address2 ?? '',
  attention: p.address.attention ?? '',
  country: p.address.country ?? '',
  mobilePhone: p.address.mobilePhone ?? '',
  postalCode: p.address.postalCode ?? '',
  postalRegion: p.address.postalRegion ?? '',
  reference: p.address.reference ?? ''
})
